/**
 * HouseholdMembersStrip — v1.0.9 direct navigation between household members.
 *
 * Rendered on the Applicant Profile when the screening belongs to a group.
 * Fetches the household via GET /groups/{gid} (backend enforces tenant/company
 * authorization — unauthorized users simply see nothing). Active members link
 * straight to their applicant profile; removed members appear greyed with a
 * "Removed" badge and remain clickable for staff history review only.
 * Pure navigation — never mutates household membership.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Users } from "lucide-react";

export default function HouseholdMembersStrip({ screening, isStaff = false }) {
  const [members, setMembers] = useState(null);
  const gid = screening.group_id;

  useEffect(() => {
    if (!gid) return;
    let live = true;
    apiClient.get(`/groups/${gid}`)
      .then(({ data }) => { if (live) setMembers(data.member_screenings || []); })
      .catch(() => { if (live) setMembers(null); });
    return () => { live = false; };
  }, [gid]);

  if (!gid || !members) return null;
  const visible = isStaff ? members : members.filter((m) => !m.removed);
  if (visible.length < 2) return null;

  return (
    <div className="mb-5" data-testid="household-members-strip">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2 flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" /> Household Members
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((m) => {
          const isCurrent = m.screening_id === screening.id;
          const roleLabel = (m.role || "").replace("_", " ");
          if (isCurrent) {
            return (
              <span key={m.screening_id} data-testid={`household-member-current-${m.screening_id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A192F] text-white text-xs">
                <strong>{m.applicant_name}</strong>
                <span className="opacity-70 font-mono uppercase tracking-[0.1em] text-[9px]">{roleLabel} · Current</span>
              </span>
            );
          }
          return (
            <Link key={m.screening_id} to={`/screenings/${m.screening_id}`}
                  data-testid={`household-member-link-${m.screening_id}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs transition-colors ${m.removed
                    ? "border-slate-200 bg-slate-50 text-slate-400 hover:border-slate-400"
                    : "border-[#0A192F]/20 bg-white text-[#0A192F] hover:border-[#0A192F]"}`}>
              <strong className={m.removed ? "line-through" : ""}>{m.applicant_name}</strong>
              <span className="font-mono uppercase tracking-[0.1em] text-[9px] opacity-70">{roleLabel}</span>
              {m.removed
                ? <span className="text-[9px] font-mono uppercase tracking-[0.1em] text-rose-500"
                        data-testid={`household-member-removed-badge-${m.screening_id}`}>Removed</span>
                : <span className="text-[9px] font-mono uppercase tracking-[0.1em]">View Applicant →</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
